import { usePostHog } from "@posthog/react";
import { Button } from "@web/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "@web/components/ui/dialog";
import { getCurrentProduct } from "@web/config";
import { useSecurityItems } from "@web/lib/api/hooks/security-items";
import { useLanguage } from "@web/lib/contexts/language-context";
import { useConsultation } from "@web/lib/hooks/consultations";
import { cn } from "@web/lib/utils";
import {
	Building2,
	CameraIcon,
	CheckCircle2,
	Database,
	Hash,
	Package,
	XCircle,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import { SecurityItemsCarousel } from "../qrcode/shared/security-items-carousel";
import type { Prediction } from "./components/detection-item";
import ExplanationSection from "./components/explanation-section";
import VerdictCard from "./components/verdict-card";
import { QueryNotFound } from "./shared/query-not-found";
import { SealModal } from "./shared/seal-modal";

export default function SealVerificationPage() {
	const [isCarouselOpen, setIsCarouselOpen] = useState(false);
	const { t, language } = useLanguage();
	const sealOwnerLabels = t.queryPage.sealOwner ?? {
		title: "Dados da empresa",
		description: "Informações da empresa responsável pelo selo.",
		cnpj: "CNPJ",
		companyName: "Nome da empresa",
		productName: "Nome do produto",
		notProvided: "Não informado",
	};
	const { QrCodeQueryLoading } = getCurrentProduct();
	const params = useParams();
	const navigate = useNavigate();
	const posthog = usePostHog();
	//force deploy
	const { data, isLoading, isFetching, isPending } = useConsultation(
		params?.id as string,
	);
	const reliabilityScore = Number(data?.queryDetails?.reliability);

	const entriesWithParsedContent =
		data?.queryEntries.map((entry) => {
			try {
				return {
					...entry,
					parsedContent: entry?.processedContent as any,
				};
			} catch (e) {
				return {
					...entry,
					parsedContent: {
						cnpj: null,
						serial: null,
						cep: null,
						register_number: null,
						phone: null,
					},
				};
			}
		}) || [];

	useEffect(() => {
		if (!data || isLoading) return;

		const storedEntries = localStorage.getItem("entriesWithParsedContent");
		const entriesArray = storedEntries
			? JSON.parse(storedEntries).filter((e: any) => e.type === "PHOTO")
			: [];

		entriesWithParsedContent
			.filter((entry) => entry.type === "PHOTO")
			.forEach((entry) => {
				const exists = entriesArray.some((e: any) => e.id === entry.id);
				if (!exists) {
					entriesArray.push(entry);
				}
			});

		localStorage.setItem(
			"entriesWithParsedContent",
			JSON.stringify(entriesArray),
		);
	}, [entriesWithParsedContent, isLoading]);

	const photoEntry =
		entriesWithParsedContent
			.filter((e) => e.type === "PHOTO" && e?.photo?.length > 0)
			.sort(
				(a, b) =>
					new Date(b?.photo[0].createdAt).getTime() -
					new Date(a?.photo[0].createdAt).getTime(),
			)[0] || null;

	const qrCodeEntry = entriesWithParsedContent.find((e) => e.type === "QRCODE");
	const isSerialMatch =
		photoEntry?.processedContent?.ocr?.serial?.value ===
		qrCodeEntry?.parsedContent?.serial;

	const productName = data?.product?.name;

	const qrCodeContent = qrCodeEntry?.parsedContent as
		| {
				product?: string;
				impressionDetails?: { cnpj?: string; name?: string };
		  }
		| undefined;

	const sealOwner = photoEntry
		? {
				cnpj:
					photoEntry.parsedContent?.ocr?.cnpj?.value ??
					qrCodeContent?.impressionDetails?.cnpj ??
					null,
				companyName: qrCodeContent?.impressionDetails?.name ?? null,
				productName: qrCodeContent?.product ?? productName ?? null,
			}
		: null;

	const { securityItems: items } = useSecurityItems({ productName });
	console.log(items);

	if (isPending || isLoading || isFetching) return <QrCodeQueryLoading />;
	if (!productName) return <QueryNotFound navigate={navigate} />;
	if (!data) return <QueryNotFound navigate={navigate} />;

	const predictions: Prediction[] =
		photoEntry?.parsedContent?.ai?.predictions || [];

	const handleGoToCamera = () => {
		try {
			navigate(`/camera?selo=${params?.id}`);
		} catch {
			toast.error("Erro ao navegar para a câmera. Por favor, tente novamente.");
		}
	};

	return (
		<div className="min-h-screen bg-background">
			{predictions.length > 0 ? (
				<>
					<div className="max-w-5xl mx-auto p-4 md:p-8 space-y-6">
						<div className="flex items-center gap-3">
							<div>
								<h1 className="text-xl font-bold">
									{t.qrCodePage.verificationResult}
								</h1>
							</div>
						</div>

						<VerdictCard
							reliabilityScore={reliabilityScore}
							status={data?.queryDetails?.publicStatus}
						/>
						{reliabilityScore < 60 && (
							<p className="text-black text-center font-normal text-sm">
								{t.qrCodePage.suspicious}
							</p>
						)}
						<div className="flex flex-col gap-6">
							<img
								src={photoEntry?.photo?.[0]?.filePath}
								className="border-2 rounded-2xl"
								alt=""
							/>
							<div className="flex flex-row items-center justify-center gap-2">
								<Database className="h-5 w-5 text-muted-foreground" />
								<p className="text-base font-semibold">
									{t.validations.details.serialNumber}
								</p>
							</div>

							<div className="flex flex-col gap-2">
								<div className="flex flex-row justify-between flex-1 gap-2">
									<div
										className={cn(
											"p-2.5 flex flex-col gap-2 rounded-2xl flex-1 border-2 transition-colors",
											isSerialMatch
												? "bg-emerald-50 border-emerald-200"
												: "bg-red-50 border-red-300",
										)}
									>
										<div className="flex items-center justify-center gap-1.5">
											{isSerialMatch ? (
												<CheckCircle2 className="h-4 w-4 text-emerald-600" />
											) : (
												<XCircle className="h-4 w-4 text-red-600" />
											)}
											<p className="text-center text-sm font-medium">
												{t.queryPage.sources.database}
											</p>
										</div>
										<div
											className={cn(
												"flex flex-row items-center gap-2 bg-white rounded-2xl p-2.5 border",
												!isSerialMatch && "border-red-200",
											)}
										>
											<Hash className="h-4 w-4 text-muted-foreground" />
											<p
												className={cn(
													"text-center justify-center text-sm font-mono",
													!isSerialMatch && "text-red-700 font-semibold",
												)}
											>
												{qrCodeEntry?.parsedContent?.serial}
											</p>
										</div>
									</div>

									<div
										className={cn(
											"p-2.5 flex flex-col gap-2 rounded-2xl flex-1 border-2 transition-colors",
											isSerialMatch
												? "bg-emerald-50 border-emerald-200"
												: "bg-red-50 border-red-300",
										)}
									>
										<div className="flex items-center justify-center gap-1.5">
											{isSerialMatch ? (
												<CheckCircle2 className="h-4 w-4 text-emerald-600" />
											) : (
												<XCircle className="h-4 w-4 text-red-600" />
											)}
											<p className="text-center text-sm font-medium">
												{t.camera.capturedPhoto}
											</p>
										</div>
										<div
											className={cn(
												"flex flex-row items-center gap-2 bg-white rounded-2xl p-2.5 border",
												!isSerialMatch && "border-red-200",
											)}
										>
											<CameraIcon className="h-5 w-5 text-muted-foreground" />
											<p
												className={cn(
													"font-mono",
													!isSerialMatch &&
														"text-red-700 text-sm font-semibold",
												)}
											>
												{photoEntry?.processedContent?.ocr?.serial?.value ||
													t.qrCodePage.serialNotFound}
											</p>
										</div>
									</div>
								</div>
								{!isSerialMatch && (
									<>
										<p className="text-sm text-red-400 font-medium text-center">
											{t.qrCodePage.hashDoesNotMatch}
										</p>
									</>
								)}
							</div>
							{productName === "SELO DE AUTENTICIDADE" && (
								<div className="bg-green-100 p-2.5 flex flex-col gap-2 rounded-2xl flex-1">
									<p className="text-center text-sm">Hash Público</p>
									<div className="flex flex-row items-center gap-2 bg-white rounded-2xl p-2.5">
										<CameraIcon className="h-5 w-5 text-muted-foreground" />
										<p>{qrCodeEntry?.processedContent?.publicHash}</p>
									</div>
								</div>
							)}

							{isSerialMatch && (
								<div className="w-full flex flex-col gap-2 justify-center items-center">
									<p className="text-center text-sm">{t.qrCodePage.info}</p>
									<div className="flex border-b w-24 text-muted-foreground" />
								</div>
							)}
						</div>

						{sealOwner && (
							<section className="rounded-2xl border bg-card p-5 shadow-sm">
								<div className="mb-4 flex items-start gap-3">
									<div className="rounded-xl bg-primary/10 p-2.5 text-primary">
										<Building2 className="h-5 w-5" />
									</div>
									<div>
										<h2 className="font-semibold">{sealOwnerLabels?.title}</h2>
										<p className="text-sm text-muted-foreground">
											{sealOwnerLabels.description}
										</p>
									</div>
								</div>

								<div className="grid gap-3 sm:grid-cols-3">
									<div className="rounded-xl border bg-muted/30 p-3">
										<p className="mb-1 flex items-center gap-2 text-xs font-medium text-muted-foreground">
											<Hash className="h-3.5 w-3.5" />
											{sealOwnerLabels.cnpj}
										</p>
										<p className="break-all font-medium">
											{sealOwner.cnpj || sealOwnerLabels.notProvided}
										</p>
									</div>
									<div className="rounded-xl border bg-muted/30 p-3">
										<p className="mb-1 flex items-center gap-2 text-xs font-medium text-muted-foreground">
											<Building2 className="h-3.5 w-3.5" />
											{sealOwnerLabels.companyName}
										</p>
										<p className="break-words font-medium">
											{sealOwner.companyName || sealOwnerLabels.notProvided}
										</p>
									</div>
									<div className="rounded-xl border bg-muted/30 p-3">
										<p className="mb-1 flex items-center gap-2 text-xs font-medium text-muted-foreground">
											<Package className="h-3.5 w-3.5" />
											{sealOwnerLabels.productName}
										</p>
										<p className="break-words font-medium">
											{sealOwner.productName || sealOwnerLabels.notProvided}
										</p>
									</div>
								</div>
							</section>
						)}

						<ExplanationSection />

						<div className="flex items-center justify-center">
							<Button
								onClick={() => {
									posthog.capture("clicked_see_all_security_items", {
										reliability: reliabilityScore,
										query_id: data?.id,
										product: data?.product?.name,
										language,
									});
									setIsCarouselOpen(true);
								}}
							>
								{t.qrCodePage.seeAllSecurityItems}
							</Button>
						</div>
					</div>

					<Dialog open={isCarouselOpen} onOpenChange={setIsCarouselOpen}>
						<DialogContent className="max-w-sm w-[90vw] rounded-2xl">
							<DialogHeader>
								<DialogTitle>{t.qrCodePage.seeAllSecurityItems}</DialogTitle>
							</DialogHeader>
							<SecurityItemsCarousel items={items as any} />
						</DialogContent>
					</Dialog>
				</>
			) : (
				<SealModal onRetry={handleGoToCamera} />
			)}
		</div>
	);
}
